import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar'; // Using Calendar for TimePicker functionality
import { useState, useEffect } from 'react';
import classes from './addRowModalShiftTimings.module.scss';
import { toast } from 'react-toastify';

interface FormData {
  shift_name: string;
  start_time: string;
  end_time: string;
}

interface AddRowModalShiftTimingProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
}

const AddRowModalShiftTiming = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit
}: AddRowModalShiftTimingProps) => {
  const [formData, setFormData] = useState<FormData>({
    shift_name: '',
    start_time: '',
    end_time: ''
  });

  // State for the Date objects used by Calendar component
  const [startTimeDate, setStartTimeDate] = useState<Date | null>(null);
  const [endTimeDate, setEndTimeDate] = useState<Date | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;

  useEffect(() => {
    if (initialValues && visible) {
      setFormData({ ...initialValues });

      // Convert string times to Date objects for the Calendar component
      if (initialValues.start_time) {
        const [hours, minutes] = initialValues.start_time.split(':').map(Number);
        const startDate = new Date();
        startDate.setHours(hours, minutes, 0);
        setStartTimeDate(startDate);
      }

      if (initialValues.end_time) {
        const [hours, minutes] = initialValues.end_time.split(':').map(Number);
        const endDate = new Date();
        endDate.setHours(hours, minutes, 0);
        setEndTimeDate(endDate);
      }

      setErrors({});
    } else if (!isEdit && visible) {
      // Reset form for new entry
      setFormData({
        shift_name: '',
        start_time: '',
        end_time: ''
      });
      setStartTimeDate(null);
      setEndTimeDate(null);
      setErrors({});
    }
  }, [initialValues, visible, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.shift_name) {
      newErrors.shift_name = 'Shift name is required';
    }
    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }
    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Only close modal on success
        onHide();
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error saving Shift Timing!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Handle time change from Calendar component
  const handleStartTimeChange = (date: Date | null) => {
    if (date) {
      // Format time as HH:MM
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}`;

      setStartTimeDate(date);
      handleInputChange('start_time', timeString);
    } else {
      setStartTimeDate(null);
      handleInputChange('start_time', '');
    }
  };

  const handleEndTimeChange = (date: Date | null) => {
    if (date) {
      // Format time as HH:MM
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}`;

      setEndTimeDate(date);
      handleInputChange('end_time', timeString);
    } else {
      setEndTimeDate(null);
      handleInputChange('end_time', '');
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit Shift Timing Configuration' : 'Add New Shift Timing Configuration'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '500px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formGroup}>
          <label>Shift Name</label>
          <InputText
            value={formData.shift_name}
            onChange={e => handleInputChange('shift_name', e.target.value)}
            placeholder="Enter shift name"
            className={`${classes.inputText} ${errors.shift_name ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.shift_name && <small className="p-error">{errors.shift_name}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Start Time</label>
          <Calendar
            value={startTimeDate}
            onChange={e => handleStartTimeChange(e.value as Date)}
            timeOnly
            hourFormat="24"
            showSeconds
            placeholder="Select start time"
            className={`${classes.calendar} ${errors.start_time ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.start_time && <small className="p-error">{errors.start_time}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>End Time</label>
          <Calendar
            value={endTimeDate}
            onChange={e => handleEndTimeChange(e.value as Date)}
            timeOnly
            hourFormat="24"
            showSeconds
            placeholder="Select end time"
            className={`${classes.calendar} ${errors.end_time ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.end_time && <small className="p-error">{errors.end_time}</small>}
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalShiftTiming;
